import { FieldTimeOutlined } from '@ant-design/icons';

import { Button, Dialog, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { ngayGioVN } from '../../../../lib/dinh-dang';
import { MAU_DONG_LOG } from '../../constants';
import { useDongThoiGian } from '../../hooks/useLichSu';
import { LoiRpa } from '../chung';
import { BadgeTrangThaiLuot, TruongThongTin } from './nhan';

/** Mức của một mốc: lỗi đỏ, thành công xanh, còn lại màu chữ thường. */
const MAU_MUC: Record<string, string> = {
  ERROR: MAU_DONG_LOG.error,
  SUCCESS: 'text-success-700',
};

/**
 * Dòng thời gian của một lượt Native `RUN-…`: mốc FOXAI (yêu cầu, gửi lệnh, kết thúc) trộn với instance OpenRPA tạo
 * gần thời điểm bắt đầu nhất và console của nó. Máy chủ đã che bí mật trong từng dòng.
 */
export function HopDongThoiGian({ runCode, onClose }: { runCode: string; onClose: () => void }) {
  const { t, locale } = useT();
  const q = useDongThoiGian(runCode);
  const ds = q.data;

  return (
    <Dialog
      open
      onClose={onClose}
      width={880}
      title={t('rpaLichSu.dongThoiGianTieuDe', { ma: runCode })}
      footer={
        <div className="flex justify-end">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.dong')}
          </Button>
        </div>
      }
    >
      {q.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : q.isError ? (
        <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
      ) : (
        <div className="space-y-3">
          {ds?.instance && (
            <dl className="grid grid-cols-1 gap-3 rounded-lg border border-default p-3 sm:grid-cols-3">
              <TruongThongTin nhan={t('rpaLichSu.instanceId')}>
                <code className="break-all font-mono text-xs">{ds.instance.instanceId || ds.instance.id}</code>
              </TruongThongTin>
              <TruongThongTin nhan={t('rpaLichSu.trangThaiInstance')}>
                <BadgeTrangThaiLuot status={ds.instance.state} />
              </TruongThongTin>
              <TruongThongTin nhan={t('rpaLichSu.may')}>{ds.instance.machineName}</TruongThongTin>
            </dl>
          )}
          {ds?.openCoreError && (
            <p role="alert" className="text-xs text-warning-700">
              {ds.openCoreError}
            </p>
          )}
          {!ds?.events.length ? (
            <Empty icon={FieldTimeOutlined} title={t('rpaLichSu.dongThoiGianRong')} className="py-6" />
          ) : (
            <ol
              tabIndex={0}
              aria-label={t('rpaLichSu.dongThoiGianTieuDe', { ma: runCode })}
              className="max-h-[55vh] divide-y divide-default overflow-auto rounded-lg border border-default font-mono text-xs"
            >
              {ds.events.map((e, i) => (
                <li key={i} className="flex flex-wrap gap-x-3 gap-y-0.5 px-3 py-1.5">
                  <span className="shrink-0 tabular-nums text-content-muted">{ngayGioVN(e.time, locale)}</span>
                  <span className="w-16 shrink-0 text-content-muted">{e.source}</span>
                  <span className={`min-w-0 flex-1 whitespace-pre-wrap break-words ${MAU_MUC[e.level] ?? MAU_DONG_LOG.normal}`}>
                    {e.message}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </Dialog>
  );
}
